import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Heading,
  Icon,
  IconButton,
  Image,
  Spacer,
  Text,
  usePrefersReducedMotion,
  useToast,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { Plus } from "lucide-react";
import { FiShoppingBag } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import { useAppDispatch } from "../../store/hooks/hooks";
import { addToCart } from "../../store/cartSlice/cartSlice";
import { useGetProductsQuery } from "../../productApi/productAPI";
import { normalizeProductForCart } from "../../utils/normalizeCartProduct";

const ShoppingBagIcon = FiShoppingBag as unknown as React.ElementType;

const cardsBorderRadii = "8px";

const mainImageReveal = keyframes`
  0% {
    opacity: 0;
    filter: saturate(0.85) blur(2px);
  }

  100% {
    opacity: 1;
    filter: saturate(1) blur(0);
  }
`;

const FALLBACK_IMAGE = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
<svg width="1200" height="900" viewBox="0 0 1200 900" fill="none" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="background" x1="90" y1="54" x2="1100" y2="846" gradientUnits="userSpaceOnUse">
      <stop stop-color="#172554"/>
      <stop offset="0.45" stop-color="#312E81"/>
      <stop offset="1" stop-color="#6D28D9"/>
    </linearGradient>
    <linearGradient id="bag" x1="390" y1="230" x2="816" y2="684" gradientUnits="userSpaceOnUse">
      <stop stop-color="white" stop-opacity="0.96"/>
      <stop offset="1" stop-color="white" stop-opacity="0.58"/>
    </linearGradient>
  </defs>
  <rect width="1200" height="900" rx="72" fill="url(#background)"/>
  <circle cx="180" cy="160" r="170" fill="white" fill-opacity="0.07"/>
  <circle cx="1030" cy="744" r="230" fill="white" fill-opacity="0.08"/>
  <path d="M388 355C388 294.249 437.249 245 498 245H702C762.751 245 812 294.249 812 355V691H388V355Z" fill="url(#bag)"/>
  <path d="M508 343C508 292.19 549.19 251 600 251C650.81 251 692 292.19 692 343" stroke="#312E81" stroke-width="34" stroke-linecap="round"/>
  <path d="M500 486H700" stroke="#312E81" stroke-width="34" stroke-linecap="round"/>
  <path d="M600 386V586" stroke="#312E81" stroke-width="34" stroke-linecap="round"/>
  <text x="600" y="786" text-anchor="middle" fill="white" font-family="Arial, sans-serif" font-size="52" font-weight="700">PickDengu Marketplace</text>
</svg>
`)}`;

export type ClassicProductShowcaseProduct = {
  id: string;
  _id?: string;
  productId?: string;

  userId?: string;
  sellerUserId?: string;
  ownerId?: string;
  sellerId?: string;
  sellerBusinessName?: string;
  businessName?: string;
  sellerName?: string;
  shopName?: string;
  dispatchWarehouseId?: string;

  title: string;
  category: string;
  image?: string;
  imageUrl?: string;
  images?: string[];
  thumbnail?: string;
  description?: string;
  price?: number | string;

  selectedVariants?: any[];
  selectedPrice?: number;
  selectedImage?: string;
  resolvedImage?: string;

  isPhysical?: boolean;
  weightKg?: number;
  lengthCm?: number;
  widthCm?: number;
  heightCm?: number;
  bulky?: boolean;

  [key: string]: any;
};

type ProductColumn = {
  category: string;
  items: ClassicProductShowcaseProduct[];
};

type ShowcaseSelection = {
  mainProduct?: ClassicProductShowcaseProduct;
  columns: ProductColumn[];
  rotationProducts: ClassicProductShowcaseProduct[];
};

type ClassicProductShowcaseProps = {
  products: ClassicProductShowcaseProduct[];
  title?: string;
  eyebrow?: string;
  description?: string;
  viewAllLabel?: string;
  viewAllPath?: string;
  rotationInterval?: number;
  autoShuffleInterval?: number;
  getProductPath?: (product: ClassicProductShowcaseProduct) => string;
};

type EdgeToEdgeProductImageProps = {
  src: string;
  fallbackSrc: string;
  alt: string;
  height: any;
  background: string;
  animate?: boolean;
  prefersReducedMotion?: boolean;
  onClick: () => void;
};

const EdgeToEdgeProductImage: React.FC<EdgeToEdgeProductImageProps> = ({
  src,
  fallbackSrc,
  alt,
  height,
  background,
  animate = false,
  prefersReducedMotion = false,
  onClick,
}) => (
  <Box
    position="relative"
    w="100%"
    minW={0}
    maxW="100%"
    h={height}
    flexShrink={0}
    overflow="hidden"
    bg={background}
    lineHeight={0}
    boxSizing="border-box"
    cursor="pointer"
    onClick={onClick}
    sx={{
      contain: "paint",
      "& img": {
        display: "block",
        width: "100%",
        minWidth: "100%",
        maxWidth: "100%",
        height: "100%",
        margin: 0,
        padding: 0,
        border: 0,
      },
    }}
  >
    <Image
      src={src}
      fallbackSrc={fallbackSrc}
      alt={alt}
      position="absolute"
      inset={0}
      w="100%"
      minW="100%"
      maxW="100%"
      h="100%"
      m={0}
      p={0}
      border={0}
      objectFit="cover"
      objectPosition="center center"
      boxSizing="border-box"
      animation={
        animate && !prefersReducedMotion
          ? `${mainImageReveal} 520ms cubic-bezier(0.2, 0.8, 0.2, 1)`
          : undefined
      }
    />
  </Box>
);

const getProductId = (product: Partial<ClassicProductShowcaseProduct>) =>
  String(product._id || product.id || product.productId || "").trim();

const slugifyCategory = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\w-]+/g, "");

const shuffleItems = <T,>(items: T[]): T[] => {
  const shuffled = [...items];

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[randomIndex]] = [
      shuffled[randomIndex],
      shuffled[index],
    ];
  }

  return shuffled;
};

const uniqueProducts = (
  products: ClassicProductShowcaseProduct[],
): ClassicProductShowcaseProduct[] => {
  const seen = new Set<string>();

  return products.filter((product) => {
    const productId = getProductId(product);

    if (!productId || seen.has(productId)) return false;

    seen.add(productId);
    return true;
  });
};

const formatPrice = (price?: number | string) => {
  if (price === undefined || price === null || price === "") {
    return "View product";
  }

  const numericPrice =
    typeof price === "number"
      ? price
      : Number(String(price).replace(/[^\d.]/g, ""));

  if (!Number.isFinite(numericPrice)) return String(price);

  return new Intl.NumberFormat("en-MW", {
    style: "currency",
    currency: "MWK",
    maximumFractionDigits: 0,
  }).format(numericPrice);
};

const buildSelection = (
  products: ClassicProductShowcaseProduct[],
): ShowcaseSelection => {
  const validProducts = uniqueProducts(
    products.filter(
      (product) =>
        Boolean(getProductId(product)) &&
        Boolean(product?.title) &&
        Boolean(product?.category),
    ),
  );

  if (!validProducts.length) {
    return {
      mainProduct: undefined,
      columns: [],
      rotationProducts: [],
    };
  }

  const grouped = new Map<string, ClassicProductShowcaseProduct[]>();

  validProducts.forEach((product) => {
    const category = product.category.trim() || "General";

    if (!grouped.has(category)) grouped.set(category, []);
    grouped.get(category)?.push(product);
  });

  const categoryEntries = Array.from(grouped.entries());

  const categoriesWithTwoOrMore = shuffleItems(
    categoryEntries.filter(([, items]) => items.length >= 2),
  );

  const smallerCategories = shuffleItems(
    categoryEntries.filter(([, items]) => items.length < 2),
  );

  /*
   * Three category columns are selected here.
   * Together with the featured card, this creates four desktop columns.
   */
  const selectedCategories = [
    ...categoriesWithTwoOrMore,
    ...smallerCategories,
  ].slice(0, 3);

  const columns = selectedCategories.map(([category, categoryProducts]) => ({
    category,
    items: shuffleItems(categoryProducts).slice(0, 2),
  }));

  const sideProducts = uniqueProducts(
    columns.flatMap((column) => column.items),
  );

  const sideIds = new Set(sideProducts.map((product) => getProductId(product)));

  const mainCandidates = validProducts.filter(
    (product) => !sideIds.has(getProductId(product)),
  );

  const mainProduct =
    shuffleItems(mainCandidates)[0] || sideProducts[0] || validProducts[0];

  const rotationProducts = uniqueProducts([
    mainProduct,
    ...sideProducts,
    ...shuffleItems(validProducts),
  ]).slice(0, 7);

  return {
    mainProduct,
    columns,
    rotationProducts,
  };
};

const ClassicProductShowcase: React.FC<ClassicProductShowcaseProps> = ({
  products,
  viewAllLabel = "View all",
  viewAllPath = "/products",
  rotationInterval = 5200,
  autoShuffleInterval = 30000,
  getProductPath = (product) => `/product/${getProductId(product)}`,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const { theme } = useCustomTheme();
  const prefersReducedMotion = usePrefersReducedMotion();

  const { data: allProducts = [] } = useGetProductsQuery();

  const [shuffleVersion, setShuffleVersion] = useState(0);
  const [activeProductId, setActiveProductId] = useState("");

  const showcaseSourceProducts = useMemo<
    ClassicProductShowcaseProduct[]
  >(() => {
    const source =
      Array.isArray(allProducts) && allProducts.length > 0
        ? allProducts
        : products;

    return uniqueProducts(
      (source || [])
        .map((product: any) => {
          const productId =
            getProductId(product) ||
            String(product?.slug || product?.title || "").trim();

          const category =
            String(product?.category || "General").trim() || "General";

          const image =
            product?.selectedImage ||
            product?.imageUrl ||
            (Array.isArray(product?.images)
              ? product.images.find(Boolean)
              : "") ||
            product?.image ||
            product?.thumbnail ||
            product?.resolvedImage ||
            "";

          return {
            ...product,
            id: productId,
            _id: product?._id || productId,
            productId: product?.productId || productId,
            title: product?.title || "Untitled Product",
            category,
            image,
            imageUrl: product?.imageUrl || image,
            description:
              product?.description ||
              `${category} product available in the PickDengu marketplace.`,
            price: product?.selectedPrice ?? product?.price,
          } as ClassicProductShowcaseProduct;
        })
        .filter(
          (product: ClassicProductShowcaseProduct) =>
            Boolean(getProductId(product)) && Boolean(product.title),
        ),
    );
  }, [allProducts, products]);

  const selection = useMemo(
    () => buildSelection(showcaseSourceProducts),
    [showcaseSourceProducts, shuffleVersion],
  );

  const fullProductById = useMemo(() => {
    const map = new Map<string, any>();

    (allProducts || []).forEach((product: any) => {
      const productId = getProductId(product);

      if (productId) {
        map.set(productId, product);
      }
    });

    return map;
  }, [allProducts]);

  useEffect(() => {
    setActiveProductId(getProductId(selection.mainProduct || {}));
  }, [selection.mainProduct, shuffleVersion]);

  useEffect(() => {
    if (autoShuffleInterval <= 0 || showcaseSourceProducts.length <= 1) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      setShuffleVersion((currentVersion) => currentVersion + 1);
    }, autoShuffleInterval);

    return () => window.clearInterval(interval);
  }, [autoShuffleInterval, showcaseSourceProducts.length]);

  useEffect(() => {
    if (
      prefersReducedMotion ||
      selection.rotationProducts.length <= 1 ||
      rotationInterval <= 0
    ) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      setActiveProductId((currentId) => {
        const currentIndex = selection.rotationProducts.findIndex(
          (product) => getProductId(product) === currentId,
        );

        const nextIndex =
          currentIndex < 0
            ? 0
            : (currentIndex + 1) % selection.rotationProducts.length;

        return getProductId(selection.rotationProducts[nextIndex]) || currentId;
      });
    }, rotationInterval);

    return () => window.clearInterval(interval);
  }, [prefersReducedMotion, rotationInterval, selection.rotationProducts]);

  const activeProduct =
    selection.rotationProducts.find(
      (product) => getProductId(product) === activeProductId,
    ) || selection.mainProduct;

  const isDark = theme.name === "dark";
  const cardBorder = theme.border;
  const cardBackground = theme.surface;
  const imageBackground = isDark
    ? "rgba(255,255,255,0.035)"
    : "rgba(15,23,42,0.025)";

  const primaryButtonBackground =
    theme.gradients?.primaryButton || theme.primary;

  const primaryButtonHover =
    theme.gradients?.primaryButtonHover || theme.primaryHover;

  const getProductImage = (product: ClassicProductShowcaseProduct) =>
    product.selectedImage ||
    product.imageUrl ||
    (Array.isArray(product.images) ? product.images.find(Boolean) : "") ||
    product.image ||
    product.thumbnail ||
    product.resolvedImage ||
    "";

  const openProduct = (product: ClassicProductShowcaseProduct) => {
    navigate(getProductPath(product));
  };

  const openCategory = (category: string) => {
    navigate(`/shop/category/${slugifyCategory(category)}`);
  };

  const handleAddToCart = (
    product: ClassicProductShowcaseProduct,
    event: React.MouseEvent,
  ) => {
    event.stopPropagation();

    const productId = getProductId(product);
    const fullProduct = fullProductById.get(productId);

    const productForCart = {
      ...(fullProduct || {}),
      ...product,
    };

    const sellerId =
      productForCart.userId ||
      productForCart.sellerUserId ||
      productForCart.ownerId ||
      productForCart.sellerId ||
      "";

    if (!sellerId) {
      toast({
        title: "Seller not found",
        description:
          "This product is missing seller information. Please open the product and try again.",
        status: "warning",
        duration: 3500,
        isClosable: true,
      });

      return;
    }

    const resolvedImage = getProductImage(productForCart);

    const rawPrice = productForCart.selectedPrice ?? productForCart.price ?? 0;
    const cardsBorderRadii = "8px";

    const resolvedPrice =
      typeof rawPrice === "number"
        ? rawPrice
        : Number(String(rawPrice).replace(/[^\d.]/g, ""));

    dispatch(
      addToCart(
        normalizeProductForCart(productForCart, {
          id: productId,
          _id: productId,
          productId,
          image: resolvedImage,
          imageUrl: resolvedImage,
          selectedImage: resolvedImage,
          selectedVariants: Array.isArray(productForCart.selectedVariants)
            ? productForCart.selectedVariants
            : [],
          selectedPrice: Number.isFinite(resolvedPrice) ? resolvedPrice : 0,
          price: Number.isFinite(resolvedPrice) ? resolvedPrice : 0,
          quantity: 1,
        }),
      ),
    );

    toast({
      title: "Added to cart",
      description: `${product.title} was added to your cart.`,
      status: "success",
      duration: 2200,
      isClosable: true,
    });
  };

  if (!activeProduct) {
    return null;
  }

  const renderAddButton = (
    product: ClassicProductShowcaseProduct,
    size: "sm" | "md",
  ) => (
    <IconButton
      aria-label={`Add ${product.title} to cart`}
      icon={
        <Box
          className="showcase-plus-icon"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          transition="transform 0.25s ease"
        >
          <Plus size={size === "md" ? 18 : 15} />
        </Box>
      }
      size={size}
      minW={size === "md" ? "42px" : "34px"}
      h={size === "md" ? "42px" : "34px"}
      borderRadius={cardsBorderRadii}
      bg={isDark ? "rgba(255,255,255,0.08)" : "gray.900"}
      color="white"
      border={`1px solid ${theme.borderStrong}`}
      boxShadow={theme.shadowSm}
      onClick={(event) => handleAddToCart(product, event)}
      _hover={{
        bg: isDark ? "rgba(255,255,255,0.14)" : "gray.800",
        transform: "translateY(-1px)",
        "& .showcase-plus-icon": {
          transform: "rotate(90deg)",
        },
      }}
      _active={{
        transform: "translateY(0)",
      }}
      transition="all 0.2s ease"
      flexShrink={0}
    />
  );

  return (
    <Box px={{ base: 3, md: 5 }} py={{ base: 3, md: 5 }}>
      <Grid
        templateColumns={{
          base: "repeat(2, minmax(0, 1fr))",
          md: "repeat(2, minmax(0, 1fr))",
          xl: "minmax(260px, 1.08fr) repeat(3, minmax(0, 1fr))",
        }}
        gap={{ base: 4, md: 5 }}
        alignItems="stretch"
      >
        <GridItem colSpan={{ base: 2, md: 2, xl: 1 }} minW={0}>
          <Flex
            direction="column"
            w="100%"
            minW={0}
            maxW="100%"
            h="100%"
            minH={{ base: "auto", xl: "100%" }}
            overflow="hidden"
            borderRadius={cardsBorderRadii}
            border={`1px solid ${cardBorder}`}
            bg={cardBackground}
            boxShadow={theme.shadowSm}
            transition="transform 0.22s ease, box-shadow 0.22s ease"
            _hover={{
              transform: "translateY(-2px)",
              boxShadow: theme.shadowMd,
            }}
          >
            <EdgeToEdgeProductImage
              key={getProductId(activeProduct)}
              src={getProductImage(activeProduct) || FALLBACK_IMAGE}
              fallbackSrc={FALLBACK_IMAGE}
              alt={activeProduct.title}
              height={{ base: "300px", md: "350px", xl: "360px" }}
              background={imageBackground}
              animate
              prefersReducedMotion={prefersReducedMotion}
              onClick={() => openProduct(activeProduct)}
            />

            <Flex
              direction="column"
              flex="1"
              p={{ base: 4, md: 5 }}
              borderTop={`1px solid ${cardBorder}`}
            >
              <Text
                color={theme.primary}
                fontSize="xs"
                fontWeight="800"
                textTransform="uppercase"
                letterSpacing="0.08em"
                noOfLines={1}
              >
                {activeProduct.category}
              </Text>

              <Flex align="flex-start" justify="space-between" gap={3} mt={2}>
                <Heading
                  color={theme.text}
                  fontSize={{ base: "xl", md: "2xl" }}
                  lineHeight="1.2"
                  noOfLines={2}
                  cursor="pointer"
                  onClick={() => openProduct(activeProduct)}
                >
                  {activeProduct.title}
                </Heading>

                {renderAddButton(activeProduct, "md")}
              </Flex>

              <Text
                mt={5}
                color={theme.text}
                fontSize={{ base: "md", sm: "lg", md: "xl" }}
                fontWeight="900"
              >
                {formatPrice(activeProduct.price)}
              </Text>

              <Button
                leftIcon={<Icon as={ShoppingBagIcon} />}
                color={theme.textOnPrimary}
                bgImage={primaryButtonBackground}
                borderRadius={cardsBorderRadii}
                boxShadow={theme.shadowSm}
                onClick={() => navigate(viewAllPath)}
                _hover={{
                  bgImage: primaryButtonHover,
                  transform: "translateY(-1px)",
                  boxShadow: theme.shadowMd,
                }}
                _active={{
                  transform: "translateY(0)",
                }}
                transition="all 0.2s ease"
              >
                {viewAllLabel}
              </Button>

              {selection.rotationProducts.length > 1 && (
                <Flex mt={4} align="center" justify="center" gap={1.5}>
                  {selection.rotationProducts.map((product) => {
                    const productId = getProductId(product);
                    const isActive = productId === getProductId(activeProduct);

                    return (
                      <Box
                        key={productId}
                        as="button"
                        type="button"
                        aria-label={`Show ${product.title}`}
                        w={isActive ? "22px" : "7px"}
                        h="7px"
                        borderRadius={cardsBorderRadii}
                        bg={isActive ? theme.primary : theme.borderStrong}
                        transition="all 0.25s ease"
                        onClick={() => setActiveProductId(productId)}
                      />
                    );
                  })}
                </Flex>
              )}
            </Flex>
          </Flex>
        </GridItem>

        {selection.columns.map((column, columnIndex) => (
          <GridItem
            key={column.category}
            minW={0}
            colSpan={{
              base: 2,
              md: 1,
              xl: 1,
            }}
          >
            <Flex
              direction="column"
              h="100%"
              p={{ base: 3, md: 4 }}
              minW={0}
              maxW="100%"
              borderRadius={cardsBorderRadii}
              border={`1px solid ${cardBorder}`}
              bg={cardBackground}
              boxShadow={theme.shadowSm}
            >
              <Heading
                color={theme.text}
                fontSize={{ base: "lg", md: "xl" }}
                lineHeight="1.25"
                noOfLines={2}
              >
                Shop {column.category}
              </Heading>

              <Flex
                direction={{ base: "row", md: "column" }}
                gap={{ base: 3, md: 5 }}
                mt={4}
                flex="1"
                align="stretch"
              >
                {column.items.map((product) => {
                  const productId = getProductId(product);
                  const isActive = productId === getProductId(activeProduct);

                  return (
                    <Flex
                      key={productId}
                      direction="column"
                      w={{ base: "auto", md: "100%" }}
                      minW={0}
                      maxW="100%"
                      flex={{ base: "1 1 0", md: "1" }}
                      minH={0}
                      overflow="hidden"
                      borderRadius={cardsBorderRadii}
                      border={`1px solid ${
                        isActive ? theme.primary : cardBorder
                      }`}
                      bg={theme.background}
                      boxShadow={
                        isActive ? theme.shadowMd : "0 1px 2px rgba(0,0,0,0.03)"
                      }
                      transition="transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease"
                      _hover={{
                        transform: "translateY(-2px)",
                        boxShadow: theme.shadowMd,
                        borderColor: theme.primary,
                      }}
                      onMouseEnter={() => setActiveProductId(productId)}
                      onFocus={() => setActiveProductId(productId)}
                    >
                      <EdgeToEdgeProductImage
                        src={getProductImage(product) || FALLBACK_IMAGE}
                        fallbackSrc={FALLBACK_IMAGE}
                        alt={product.title}
                        height={{
                          base: "145px",
                          sm: "170px",
                          md: "170px",
                          xl: "150px",
                        }}
                        background={imageBackground}
                        onClick={() => openProduct(product)}
                      />

                      <Flex
                        direction="column"
                        flex="1"
                        p={{ base: 2.5, sm: 3 }}
                        borderTop={`1px solid ${cardBorder}`}
                      >
                        <Flex
                          align="flex-start"
                          justify="space-between"
                          gap={2.5}
                        >
                          <Box minW={0}>
                            <Text
                              color={theme.text}
                              fontSize={{ base: "xs", sm: "sm" }}
                              fontWeight="800"
                              lineHeight="1.3"
                              noOfLines={2}
                              cursor="pointer"
                              onClick={() => openProduct(product)}
                            >
                              {product.title}
                            </Text>
                          </Box>

                          {renderAddButton(product, "sm")}
                        </Flex>

                        <Spacer />

                        <Flex
                          mt={3}
                          align="center"
                          justify="space-between"
                          gap={2}
                        >
                          <Text
                            color={theme.text}
                            fontSize={{ base: "xs", sm: "sm" }}
                            fontWeight="900"
                            noOfLines={1}
                          >
                            {formatPrice(product.price)}
                          </Text>

                          <Button
                            size="xs"
                            px={3}
                            minW="auto"
                            color={theme.textOnPrimary}
                            bgImage={primaryButtonBackground}
                            borderRadius="5px"
                            onClick={() => openProduct(product)}
                            _hover={{
                              bgImage: primaryButtonHover,
                              transform: "translateY(-1px)",
                            }}
                            _active={{
                              transform: "translateY(0)",
                            }}
                            transition="all 0.2s ease"
                          >
                            View
                          </Button>
                        </Flex>
                      </Flex>
                    </Flex>
                  );
                })}
              </Flex>
            </Flex>
          </GridItem>
        ))}
      </Grid>
    </Box>
  );
};

export default ClassicProductShowcase;
